import { RRule, rrulestr } from "rrule";

import { generateFrequencyString, FREQUENCIES } from "../../../../Recurrence";
import Filters from "../../../../filters";
import Account from "../../../../Account";

import { DisplayStatus } from "./consts";

import type { FormattedBulkPayment } from "../bulkPayments";
import type { FilterQueryType } from "./types";

export const formatPaymentActivity = (
  activity: API.PaymentActivity,
  recipients: API.Recipient[],
  accounts: Array<API.AnyAccount | Account>,
): API.FormattedPaymentActivity => {
  const recipient = recipients.find((r) => r.id === activity.recipient);
  const institutionAccount = accounts.find(
    (a) => a.id === activity.institution_account,
  );

  const getRruleModel = () => {
    const { rrule: rruleString } = activity;
    if (!rruleString) return null;
    return rrulestr(rruleString);
  };

  const getRecurrence = (rruleModel: RRule | null) => {
    if (!rruleModel) return null;

    // user inputted "count" (ends after X amount of payments)
    if (rruleModel.options.count) {
      return rruleModel.options.count;
    }

    // there's an end date, so we have to calculate the count
    if (rruleModel.options.until) {
      return rruleModel.count();
    }

    // if infinite payment, it will return null
    return null;
  };

  const getStartDate = (rruleModel: RRule | null) => {
    if (!rruleModel) return null;
    return rruleModel.options.dtstart;
  };

  const getFormattedAccount = (
    account: API.AnyAccount | Account | undefined,
  ) => {
    if (account instanceof Account) {
      return account;
    }
    if (account) {
      return Account.deserialize(account);
    }
    return null;
  };

  const {
    object_uuid: uuid,
    model,
    amount,
    description,
    parent,
    original_payment: originalPayment,
    reversal,
    is_reversal: isReversal,
    rrule,
    scheduled_date: scheduledDate,
    state,
    dual_approval: dualApprovalId,
    bulk_payment: bulkPaymentId,
    wire_import_batch: wireImportBatchId,
    is_same_day: isSameDay,
    direction,
    is_amount_estimated: isAmountEstimated,
    destination_details: destinationDetails,
    invoice,
    user_uuid: userUuid,
    is_reversible: isReversible,
  } = activity;

  const activityState = activity?.state ?? "unknown";
  const displayStatus =
    DisplayStatus[activityState as keyof typeof DisplayStatus];
  const rawFrequency = rrule
    ? generateFrequencyString(RRule.fromString(rrule))
    : null;
  const frequency = rawFrequency === FREQUENCIES.ONCE ? null : rawFrequency;
  const activityAmount = amount;
  const currencyAmountDollars =
    activityAmount === null
      ? ""
      : Filters.currency(activityAmount, {
          hasDecimal: false,
        });
  const rruleModel = getRruleModel();
  const recurrence = getRecurrence(rruleModel);
  const formattedAccount = getFormattedAccount(institutionAccount);
  const startDate = getStartDate(rruleModel);
  const positiveDisplayAmountDollars = `${currencyAmountDollars}`;
  const isSameDayPayment =
    isSameDay &&
    Boolean(
      model === "UnverifiedACHTransaction" || model === "UnverifiedACHTransfer",
    );
  const isInstantPayment = model === "FedNowTransaction";
  const isFedWirePayment = model === "WireTransaction";
  const isInternationalWirePayment =
    model === "SwiftWireTransaction" || model === "SwiftWireTransfer";
  const isCollectPayment = direction === "debit";
  const isSendPayment = direction === "credit";
  const formattedAmount =
    activityAmount === null
      ? ""
      : `${isCollectPayment ? "+" : "-"}${currencyAmountDollars}`;

  return {
    uuid,
    model,
    amountDollars: Filters.penniesToDollars(activityAmount ?? 0),
    displayAmountDollars: formattedAmount,
    amount: activityAmount ?? (0 as Cents),
    description,
    institutionAccount: formattedAccount,
    parent,
    originalPayment,
    reversal,
    frequency,
    scheduledDate,
    state,
    recipient,
    recipientName: recipient?.name || activity.recipient_name || "",
    displayStatus,
    dualApprovalId,
    recurrence,
    rruleModel,
    startDate,
    bulkPaymentId,
    wireImportBatchId,
    positiveDisplayAmountDollars,
    isSameDayPayment,
    isInstantPayment,
    isFedWirePayment,
    isCollectPayment,
    isSendPayment,
    isInternationalWirePayment,
    isReversal,
    isAmountEstimated: activityAmount === null ? false : isAmountEstimated,
    destinationDetails,
    invoice,
    userId: userUuid,
    isReversible,
  };
};

export const getOriginalPaymentDirection = (
  payment: Pick<
    API.FormattedPaymentActivity,
    "isCollectPayment" | "isReversal"
  >,
): "collect" | "send" => {
  const ownDirection = payment.isCollectPayment ? "collect" : "send";
  if (!payment.isReversal) {
    return ownDirection;
  }
  return ownDirection === "collect" ? "send" : "collect";
};

export const showAmountStrikeThrough = (
  displayStatus:
    | API.FormattedPaymentActivity["displayStatus"]
    | FormattedBulkPayment["displayStatus"],
) => {
  return (
    displayStatus === DisplayStatus.failed ||
    displayStatus === DisplayStatus.canceled
  );
};

export type PaymentCancelOptions = {
  currentUserUuid?: API.UserId;
  isDomesticWireDualApprovalRequired?: boolean;
  isInternationalWireDualApprovalRequired?: boolean;
  isAchReversalDualApprovalRequired?: boolean;
  isHoldCancelable?: boolean;
  isScheduleCancelable?: boolean;
};

export type PaymentCancelAction = {
  kind:
    | "scheduledACH"
    | "dualApproval"
    | "consumerSwiftWire"
    | "scheduledSwiftWire";
  id: API.FormattedPaymentActivity["uuid"] | API.DualApprovalId;
  model: API.FormattedPaymentActivity["model"];
  usesDualApproval: boolean;
};

export const resolveCancelAction = (
  paymentActivity: Pick<
    API.FormattedPaymentActivity,
    | "uuid"
    | "model"
    | "displayStatus"
    | "dualApprovalId"
    | "userId"
    | "isReversal"
    | "isSameDayPayment"
  >,
  options?: PaymentCancelOptions,
): PaymentCancelAction | null => {
  const {
    uuid,
    model,
    displayStatus,
    dualApprovalId,
    userId,
    isReversal,
    isSameDayPayment,
  } = paymentActivity;

  // dual approval cancellation is restricted to the requester
  const isRequester =
    Boolean(options?.currentUserUuid) &&
    Boolean(userId) &&
    options?.currentUserUuid === userId;
  const isPendingApproval =
    displayStatus === DisplayStatus.pending_approval &&
    Boolean(dualApprovalId) &&
    isRequester;

  const isWirePendingApproval =
    isPendingApproval &&
    ((model === "WireTransaction" &&
      options?.isDomesticWireDualApprovalRequired) ||
      (model === "SwiftWireTransaction" &&
        options?.isInternationalWireDualApprovalRequired));

  const isACHReversalPendingApproval =
    isPendingApproval &&
    Boolean(options?.isAchReversalDualApprovalRequired) &&
    isReversal;
  const isSameDayACHPendingApproval =
    isPendingApproval &&
    model === "UnverifiedACHTransaction" &&
    isSameDayPayment &&
    !isReversal;

  // pending approvals are cancelled via their dual approval
  if (
    isWirePendingApproval ||
    isACHReversalPendingApproval ||
    isSameDayACHPendingApproval
  ) {
    return {
      kind: "dualApproval",
      id: dualApprovalId,
      model,
      usesDualApproval: true,
    };
  }

  // consumer international wires are cancelable during the backend hold window
  if (
    model === "SwiftWireTransaction" &&
    isRequester &&
    options?.isHoldCancelable === true
  ) {
    return {
      kind: "consumerSwiftWire",
      id: uuid,
      model,
      usesDualApproval: false,
    };
  }

  if (
    model === "SwiftWireTransfer" &&
    isRequester &&
    options?.isScheduleCancelable === true
  ) {
    return {
      kind: "scheduledSwiftWire",
      id: uuid,
      model,
      usesDualApproval: false,
    };
  }

  const isScheduledACH =
    displayStatus !== DisplayStatus.canceled &&
    displayStatus !== DisplayStatus.completed &&
    model === "UnverifiedACHTransfer";

  if (isScheduledACH) {
    // a scheduled payment that is itself a reversal is still cancelled through
    // its dual approval, even before it reaches the pending approval state
    if (isReversal && Boolean(dualApprovalId)) {
      return {
        kind: "dualApproval",
        id: dualApprovalId,
        model,
        usesDualApproval: true,
      };
    }

    return { kind: "scheduledACH", id: uuid, model, usesDualApproval: false };
  }

  return null;
};

export const showCancelOption = (
  paymentActivity: API.FormattedPaymentActivity,
  options?: PaymentCancelOptions,
) => resolveCancelAction(paymentActivity, options) !== null;

export const getUntilEndString = (rruleModel: RRule) => {
  const allOccurences = rruleModel.all();
  return Filters.longMonthDayYear(
    allOccurences[allOccurences.length - 1].toISOString(),
  );
};

export const getTotalAmount = (recurrence: number, amountDollars: Dollars) => {
  return Filters.currency(
    (recurrence * Filters.dollarsToPennies(amountDollars)) as Cents,
  );
};
// filters out empty arrays, undefined, null or empty string
// returns something like status=approved,pending&amount_max=10000
export const convertFiltersToQueryString = (
  filterObj: Record<string, any>,
): string => {
  const {
    selected_template_ids: selectedTemplateIds,
    selected_template_version_ids: selectedTemplateVersionIds,
    ...remainingFilters
  } = filterObj;

  const normalizedFilters = { ...remainingFilters };

  // Atlas-only: template filters are stored locally under selected_* keys.
  // Translate them to backend-supported params (template_version or template).
  if (
    Array.isArray(selectedTemplateVersionIds) &&
    selectedTemplateVersionIds.length > 0
  ) {
    normalizedFilters.template_version = selectedTemplateVersionIds;
  } else if (
    Array.isArray(selectedTemplateIds) &&
    selectedTemplateIds.length === 1
  ) {
    normalizedFilters.template = selectedTemplateIds[0];
  }

  const entries = Object.entries(normalizedFilters).filter(([, value]) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  });

  const queryStringParts = entries.map(([key, value]) => {
    const encodedKey = encodeURIComponent(key);

    if (Array.isArray(value)) {
      const encodedValues = value.map((v) => encodeURIComponent(v)).join(",");
      return `${encodedKey}=${encodedValues}`;
    }

    const encodedValue = encodeURIComponent(value);
    return `${encodedKey}=${encodedValue}`;
  });
  return queryStringParts.join("&");
};

export const buildQueryString = (
  filters: FilterQueryType,
  search: string,
): string => {
  const filterQuery = filters ? convertFiltersToQueryString(filters) : "";
  const encodedSearch = search ? `search=${encodeURIComponent(search)}` : "";

  return [filterQuery, encodedSearch].filter(Boolean).join("&");
};
